import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import type { Country } from '@freelancer/datastore/collections';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type {
  LocationInputCustomAddress,
  LocationInputCustomCoordinate,
  LocationInputMapLocation,
} from '@freelancer/ui/location-input';
import {
  AutocompleteLocationType,
  LocationInputComponent,
  isLocationInputMapLocation,
} from '@freelancer/ui/location-input';
import { Focus } from '../focus';
import { InputSize } from '../input';
import { FontColor, FontType } from '../text';
import { MultipleLocationInputSize } from './multiple-location-input.types';

@Component({
  selector: 'fl-multiple-location-input',
  template: `
    <div class="Location">
      <div
        class="Location-item"
        [attr.data-input-size]="inputSize"
      >
        <fl-icon
          class="Location-icon"
          [attr.data-input-size]="inputSize"
          [color]="IconColor.SECONDARY"
          [name]="'ui-pin-location'"
          [flMarginRight]="'xxsmall'"
          [size]="IconSize.SMALL"
        ></fl-icon>
        <div class="Location-address">
          <fl-location-input
            #firstLocation
            class="Location-input"
            flTrackingLabel="FirstLocation"
            [autocompleteCountries]="autocompleteCountries"
            [control]="firstLocationControl"
            [countryRestrictions]="countryRestrictions"
            [detectLocation]="detectLocation"
            [displayPostalCode]="true"
            [dynamicValidation]="dynamicValidation"
            [flMarginRight]="'mid'"
            [inputSize]="inputSize"
            [locationType]="locationType"
            [placeholder]="firstLocationPlaceholder"
          ></fl-location-input>
        </div>
      </div>
      <div
        class="Location-item"
        [attr.data-input-size]="inputSize"
      >
        <fl-icon
          *ngIf="!hasNewLocation"
          class="Location-icon LocationAddIcon"
          flTrackingLabel="SecondLocationIcon"
          label="Add second location"
          i18n-label="Add second location clickable icon"
          [attr.data-input-size]="inputSize"
          [clickable]="true"
          [flMarginRight]="'xxsmall'"
          [name]="'ui-add-circle'"
          [size]="IconSize.SMALL"
          (click)="handleNewLocation()"
        ></fl-icon>
        <fl-icon
          *ngIf="hasNewLocation"
          class="Location-icon"
          [attr.data-input-size]="inputSize"
          [color]="IconColor.ERROR"
          [name]="'ui-pin-location'"
          [flMarginRight]="'xxsmall'"
          [size]="IconSize.SMALL"
        ></fl-icon>
        <div class="Location-address">
          <fl-button
            *ngIf="!hasNewLocation"
            class="Location-fakeInput"
            flTrackingLabel="FakeLocationInput"
            i18n="Enter second location placeholder"
            label="Add second location"
            i18n-label="Add second location button"
            [attr.data-input-size]="inputSize"
            [flMarginRight]="'mid'"
            (click)="handleNewLocation()"
          >
            <fl-text
              [fontType]="FontType.CONTAINER"
              [color]="FontColor.INHERIT"
            >
              {{ secondLocationPlaceholder }}
            </fl-text>
          </fl-button>
          <fl-location-input
            #secondLocation
            *ngIf="hasNewLocation"
            class="Location-input"
            flTrackingLabel="SecondLocation"
            [autocompleteCountries]="autocompleteCountries"
            [control]="secondLocationControl"
            [countryRestrictions]="countryRestrictions"
            [detectLocation]="detectLocation"
            [displayPostalCode]="true"
            [dynamicValidation]="dynamicValidation"
            [inputSize]="inputSize"
            [locationType]="locationType"
            [placeholder]="secondLocationPlaceholder"
            (clearLocation)="handleSecondClearLocation()"
          ></fl-location-input>
          <fl-icon
            *ngIf="hasNewLocation"
            class="Location-swapIcon"
            flTrackingLabel="SwapLocations"
            i18n-label="Clickable icon to swap the two locations"
            label="Swap the two locations"
            [attr.data-input-size]="inputSize"
            [clickable]="true"
            [name]="'ui-switch'"
            [ngStyle]="{ visibility: hasNewLocation ? 'visible' : 'hidden' }"
            [size]="IconSize.MID"
            (click)="handleSwapLocation()"
          ></fl-icon>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./multiple-location-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultipleLocationInputComponent implements OnInit {
  FontColor = FontColor;
  FontType = FontType;
  IconColor = IconColor;
  IconSize = IconSize;
  InputSize = InputSize;

  @Input() firstLocationControl: FormControl<
    | LocationInputCustomCoordinate
    | LocationInputCustomAddress
    | LocationInputMapLocation
    | undefined
  >;
  @Input() secondLocationControl: FormControl<
    | LocationInputCustomCoordinate
    | LocationInputCustomAddress
    | LocationInputMapLocation
    | undefined
  >;

  @Input() firstLocationPlaceholder = $localize`Enter a location`;
  @Input() secondLocationPlaceholder = $localize`Enter a second location`;
  @Input() detectLocation = true;
  /** First validation happens only on blur, and validates on value changes once an error exists */
  @Input() dynamicValidation = false;
  @Input() isSecondLocationRequired = false;
  @Input() locationType: AutocompleteLocationType =
    AutocompleteLocationType.ALL;
  @Input() countryRestrictions?: readonly Country[];
  /**
   * Currently only supports LARGE and MID values.
   * Additional sizes require scss modifications
   */
  @Input() inputSize: MultipleLocationInputSize = InputSize.MID;
  /** Country restrictions override for autocomplete predictions. */
  @Input() autocompleteCountries?: string | string[];

  @ViewChild('firstLocation') firstLocation: LocationInputComponent;
  @ViewChild('secondLocation') secondLocation: LocationInputComponent;

  hasNewLocation = false;

  constructor(
    private cd: ChangeDetectorRef,
    private focus: Focus,
  ) {}

  ngOnInit(): void {
    this.hasNewLocation =
      this.isSecondLocationRequired || !!this.secondLocationControl.value;
  }

  handleNewLocation(): void {
    this.hasNewLocation = true;
    // To re-render and display `secondLocation` once `hasNewLocation` === true

    // D211210 - Keeping as detectChanges() as using markForCheck() since the event happens in a different level of the tree.
    // eslint-disable-next-line local-rules/no-detectchanges-without-detach-reattach
    this.cd.detectChanges();
    this.focus.focusElement(this.secondLocation.input.nativeElement);
  }

  handleSwapLocation(): void {
    const firstLocationValue = this.firstLocationControl.value;
    const secondLocationValue = this.secondLocationControl.value;
    if (
      secondLocationValue &&
      isLocationInputMapLocation(secondLocationValue) &&
      secondLocationValue.location.fullAddress
    ) {
      this.firstLocation.nativeControl.setValue(
        secondLocationValue.location.fullAddress,
      );
      this.firstLocation.setLocationControl(secondLocationValue);
      this.firstLocation.nativeControl.setErrors(null);
      this.firstLocation.error = undefined;
    } else {
      this.firstLocation.nativeControl.reset();
    }

    if (
      firstLocationValue &&
      isLocationInputMapLocation(firstLocationValue) &&
      firstLocationValue.location.fullAddress
    ) {
      this.secondLocation.nativeControl.setValue(
        firstLocationValue.location.fullAddress,
      );
      this.secondLocation.setLocationControl(firstLocationValue);
      this.secondLocation.nativeControl.setErrors(null);
      this.secondLocation.error = undefined;
    } else {
      this.secondLocation.nativeControl.reset();
    }
  }

  handleSecondClearLocation(): void {
    this.hasNewLocation = this.isSecondLocationRequired;
  }
}
